/**
 * Seasonal Highlights Component for v2v2 SWFL Farmers Market Application
 * Compact component showing top items in season for sidebars/footers
 */



import { Leaf, Calendar, ArrowRight } from 'lucide-react';
import { useSeasonal } from '@/hooks/use-seasonal';
import { cn } from '@/lib/utils';

interface SeasonalHighlightsProps {
  className?: string;
  limit?: number;
  showViewAll?: boolean;
  variant?: 'compact' | 'detailed';
}

export function SeasonalHighlights({
  className,
  limit = 5,
  showViewAll = true,
  variant = 'compact',
}: SeasonalHighlightsProps) {
  const {
    currentMonth,
    seasonalHighlights,
    peakProduce,
    isLoading,
    getSeasonLabel,
    currentSeason,
  } = useSeasonal();

  const items = variant === 'compact' 
    ? seasonalHighlights.slice(0, limit)
    : peakProduce.slice(0, limit);

  if (isLoading) {
    return (
      <div className={cn('animate-pulse space-y-2', className)}>
        <div className="h-6 bg-muted rounded w-1/2" />
        <div className="space-y-2">
          {Array.from({ length: limit }).map((_, i) => (
            <div key={i} className="h-8 bg-muted rounded" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={cn('space-y-3', className)}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Leaf className="h-5 w-5 text-green-600" />
          <h3 className="font-semibold">In Season Now</h3>
        </div>
        <span className="text-xs text-muted-foreground">
          {currentMonth}
        </span>
      </div>

      {/* Current Season Badge */}
      <div className="flex items-center gap-2">
        <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-800 font-medium">
          {getSeasonLabel(currentSeason)}
        </span>
      </div>

      {/* Produce List */}
      {variant === 'compact' ? (
        <ul className="space-y-2">
          {items.map((item, index) => (
            <li key={item} className="flex items-center gap-2">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-green-100 text-green-700 text-xs flex items-center justify-center font-medium">
                {index + 1}
              </span>
              <span className="text-sm">{item}</span>
            </li>
          ))}
        </ul>
      ) : (
        <div className="flex flex-wrap gap-2">
          {items.map((item) => (
            <span
              key={item}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-green-50 text-green-800 text-sm border border-green-200"
            >
              <Leaf className="h-3 w-3" />
              {item}
            </span>
          ))}
        </div>
      )}

      {/* View All Link */}
      {showViewAll && (
        <button className="w-full flex items-center justify-center gap-1 py-2 text-sm text-green-700 hover:text-green-800 transition-colors">
          <Calendar className="h-4 w-4" />
          View Full Calendar
          <ArrowRight className="h-3 w-3" />
        </button>
      )}

      {/* Additional Info */}
      <div className="pt-2 border-t">
        <p className="text-xs text-muted-foreground">
          {peakProduce.length} peak items this month
        </p>
      </div>
    </div>
  );
}

/**
 * Mini seasonal indicator for footer use
 */
interface MiniSeasonalIndicatorProps {
  className?: string;
}

export function MiniSeasonalIndicator({ className }: MiniSeasonalIndicatorProps) {
  const { currentMonth, seasonalHighlights, isLoading } = useSeasonal();

  if (isLoading) {
    return (
      <div className={cn('animate-pulse h-8 bg-muted rounded', className)} />
    );
  }

  const topItem = seasonalHighlights[0];

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <Leaf className="h-4 w-4 text-green-600" />
      <span className="text-sm">
        {currentMonth}: <strong>{topItem || 'Season'}</strong>
      </span>
    </div>
  );
}
